import Component from "./components/component";
import MapComponent, { MapComponentEvents } from "./components/map/map";
import { Configuration, getConfig, setConfig } from "./configuration/config";
import {
  SYSTEM_LANG,
  setUserLocale,
  getLocaleLang,
  getLocale,
} from "./helpers/locale";
import { preloadLink } from "./utils/preload";
import {
  getFeaturePosition,
  showToast,
  computePaddingForPOIPanel,
  isMobileView,
  displayBuilding,
} from "./utils/utils";
import ThematicComponent, {
  ThematicComponentEvents,
} from "./components/thematicSelector/thematicSelector";
import KioskComponent, {
  KioskComponentEvents,
} from "./components/kiosk/kioskView";
import { SOUTH_PAVILION_REFS } from "../data/kiosk";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import "./sdk/styles/ui.styl";
export interface IIndoorComponent {
  locale?: string;
  config?: Configuration;
  query?: string;
  source?: string;
  reset?: boolean;
}
export interface Building {
  ref: string;
  pavilion: string[];
  title: string;
  center: woosmap.map.LatLngLiteral;
  kioskZoom?: number;
}
export enum WidgetEvents {
  AUTOCOMPLETE = "AUTOCOMPLETE",
  POI_SELECTED = "POI_SELECTED",
  PHONE_CLICKED = "PHONE_CLICKED",
  WEBSITE_CLICKED = "WEBSITE_CLICKED",
  SEARCH_RESULT_ITEM_SELECTED = "SEARCH_RESULT_ITEM_SELECTED",
  FIND_ROUTE = "FIND_ROUTE",
  NAVIGATION_STARTED = "NAVIGATION_STARTED",
  ITINERARY_MODE_EXITED = "ITINERARY_MODE_EXITED",
}
export enum Page {
  EXHIBITORS = "exhibitors",
  THEMATIC = "thematic",
  KIOSK = "kiosk",
  GENERAL = "general",
}
export interface POIForAnalytics {
  item_id: string;
  item_name: string;
  item_category: string;
  quantity: number;
}
export interface SearchResult {
  ref: string;
  name: string;
  id: string;
}
export interface Kiosk {
  lat: number;
  lng: number;
  title: string;
  ref: number;
}
interface Step {
  level: number;
  properties: {
    level: number | null;
  };
}

export enum DefaultMapOptions {
  tilt = 80,
  heading = -14.39,
  centerLat = 45.51908,
  centerLng = 9.08109,
  zoom = 17,
  minZoomAllowed = 14,
  kioskZoom = 20,
  kioskTilt = 45,
  kioskHeading = 26.39,
}
export interface FavPOI {
  id: string | number | undefined;
  position: woosmap.map.LatLngLiteral | woosmap.map.LatLng;
  level: number;
  properties: { [key: string]: string };
}
export interface GeoJSONFeature {
  geometry: woosmap.map.GeoJSONGeometry;
  id?: number | string;
  properties: { [key: string]: string };
  type: "Feature";
}
export enum ExhibitorStatus {
  NONE = "none",
  TO_VISIT = "tovisit",
  VISITED = "visited",
}
interface ExhibitorStatusMapping {
  exhibitor_id: string;
  status: ExhibitorStatus;
}

export default class IndoorMapView extends Component<IIndoorComponent> {
  public mapComponent!: MapComponent;
  public kioskComponent!: any;
  public thematicComponent!: any;
  public venue!: woosmap.map.Venue;
  public selectedThematic!: string;
  public isKiosk: boolean = false;
  public currentKiosk!: Kiosk;
  public currentPage: string = Page.GENERAL;
  public itemsForThematicSelected!: POIForAnalytics[];
  public thematicCenter = {
    lat: DefaultMapOptions.centerLat,
    lng: DefaultMapOptions.centerLng,
  };
  public loggedInUser: string = "";
  public infoWindow!: woosmap.map.InfoWindow;
  public mapType: string = "general";
  public selectedOrigin!: woosmap.map.GeoJSONFeature | undefined;
  public selectedDestination!: woosmap.map.GeoJSONFeature | undefined;
  init(): void { }
  render(): void {
    setUserLocale(this.state.locale || SYSTEM_LANG);
    setConfig(this.state.config);
    this.preloadLinks();
    this.mapComponent = new MapComponent({
      $target: document.getElementById("app") as HTMLElement,
      initialState: {
        woosmapPublicKey: getConfig().map.woosmapPublicKey,
        mapOptions: getConfig().map.mapOptions,
        indoorRendererOptions: getConfig().map.indoorRendererOptions,
        indoorWidgetOptions: getConfig().map.indoorWidgetOptions,
        query: this.state.config?.map.query,
      },
    });
    this.mapComponent.on(MapComponentEvents.MAP_READY, () => {
      this.mapComponent.initIndoorView();
      this.loadGoogleTagManager();
    });
    this.mapComponent.on(MapComponentEvents.MAP_IDLE, () => {
      if (this.mapComponent.map.getZoom() < DefaultMapOptions.minZoomAllowed) {
        if (this.currentPage === Page.KIOSK && this.currentKiosk) {
          const kiosk = this.currentKiosk as any;
          this.setBoundsForSelectedPav(
            kiosk.ref,
            { lat: kiosk.lat, lng: kiosk.lng },
            kiosk.isSouth ?? false,
            kiosk.zoom,
            kiosk.panOffset,
            kiosk.xPanOffset,
          );
        } else {
          this.mapComponent.map.setZoom(DefaultMapOptions.zoom);
          this.mapComponent.map.panTo({
            lat: DefaultMapOptions.centerLat,
            lng: DefaultMapOptions.centerLng,
          });
        }
      }
    });
    this.mapComponent.on(
      MapComponentEvents.INDOOR_VENUE_LOADED,
      (venue: woosmap.map.Venue) => {
        // TO DO: KEEP UNTIL IT GOES TO PROD
        console.log("Venue loaded ", venue);
        this.venue = JSON.parse(JSON.stringify(venue));
        const width =
          window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth;
        // Do any actions here when indoor venue is fully loaded
        if (
          this.state.query &&
          this.state.query !== "" &&
          this.state.query.includes("kiosk")
        ) {
          this.currentPage = Page.KIOSK;
          this.mapComponent.indoorMap.setWidgetMapMode("kiosk");
          this.fetchKiosksData()
            .then((kiosks) => {
              const queryParam = this.state.query?.split("=");
              if (queryParam && queryParam.length > 0 && kiosks) {
                const filteredKiosk: any = kiosks?.find(
                  (item: any) =>
                    item.title.toLowerCase() === queryParam[1].toLowerCase(),
                );
                this.isKiosk = true;
                this.currentKiosk = filteredKiosk;
                if (filteredKiosk) {
                  this.mapComponent.setOrigin(filteredKiosk);
                  this.selectedOrigin = {
                    type: "Feature",
                    id: -1,
                    geometry: {
                      type: "Point",
                      coordinates: [filteredKiosk.lng, filteredKiosk.lat],
                    },
                    properties: {
                      level: "0",
                      name: getLocale().indoor["Your Location"],
                      pk: "-1",
                    },
                  };
                  this.setBoundsForSelectedPav(filteredKiosk.ref, { lat: filteredKiosk.lat, lng: filteredKiosk.lng }, filteredKiosk.isSouth, filteredKiosk.zoom, filteredKiosk.panOffset, filteredKiosk.xPanOffset);
                }
              }
            })
            .catch((error) => {
              console.error("Error in reading kiosk data", error);
            });
          this.mapComponent.turnOffNavigation();
          if (this.kioskComponent === undefined) {
            this.kioskComponent = new KioskComponent({
              $target: document.getElementById("wrapper") as HTMLElement,
              initialState: {
                query: this.state.query,
              },
            });
            this.displayKioskElements();
            this.restrictKioskUserInteractions();
            this.kioskComponent.initCustomKeyboard();
            this.kioskComponent.disableSystemKeyboard();
            this.mapComponent.indoorMap.setIsAutofocusDisabled(true);

            this.kioskComponent.on(
              KioskComponentEvents.LOGO_CLICKED,
              (ref: string) => {
                if (this.currentKiosk) {
                  this.mapComponent.showRouteForSponsor(
                    { lat: this.currentKiosk.lat, lng: this.currentKiosk.lng },
                    ref,
                  );
                }
              },
            );
            this.kioskComponent.on(
              KioskComponentEvents.LANGUAGE_CHANGED,
              (lang: string) => {
                this.setState({ locale: lang });
              },
            );
            this.kioskComponent.on(KioskComponentEvents.MAP_RELOAD, () => {
              this.mapComponent.destroyMap();
              this.mapComponent.indoorMap.setIsAutofocusDisabled(true);
              const btnAllExhibits = document.getElementById("btnAllExhibits");
              if (btnAllExhibits) {
                btnAllExhibits.classList.add("active");
              }
              const btnThematic = document.getElementById("btnThematic");
              if (btnThematic) {
                btnThematic.classList.remove("active");
              }
            });
          }
        }
        if (
          this.state.query &&
          this.state.query !== "" &&
          this.state.query.includes("thematic")
        ) {
          const selectedTheme = this.state.query.split("=")[1];
          this.selectedThematic = selectedTheme;
          this.itemsForThematicSelected = [];
          if (
            selectedTheme !== getConfig().map.themes["default"] &&
            selectedTheme !== getConfig().map.themes["favorites"]
          ) {
            this.mapComponent.setFilter(selectedTheme);
          }

          window.dataLayer = window.dataLayer || [];
          if (
            this.selectedThematic &&
            this.selectedThematic !== getConfig().map.themes["default"] &&
            this.selectedThematic !== getConfig().map.themes["favorites"]
          ) {
            this.getPOIExtendedData(undefined, this.state.query)
              .then((pois) => {
                window.dataLayer = window.dataLayer || [];
                this.itemsForThematicSelected = pois;
                window.dataLayer.push({
                  event: "woo_filter",
                  click_location: undefined,
                  content_type: "thematic",
                  content_id: this.selectedThematic,
                  parent_content_type: undefined,
                  parent_content_id: undefined,
                  items: pois,
                  _clear: true,
                  search_term: undefined,
                  item_name: undefined,
                  nav_from: undefined,
                  nav_to: undefined,
                });
              })
              .catch((error) => {
                console.error("Error while searching POIs:", error);
              });
          }
        }
        this.initThematicComponent();
      },
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.mapComponent.on(
      MapComponentEvents.INDOOR_WIDGET_ANALYTICS,
      (event: any) => {
        if (
          this.state.query &&
          this.state.query !== "" &&
          this.state.query.includes("kiosk")
        ) {
          this.currentPage = Page.KIOSK;
          this.kioskComponent.startSessionCountdown();
        }
        if (
          event.event === WidgetEvents.AUTOCOMPLETE &&
          event.args.query.length >= 3
        ) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "woo_search",
            click_location: undefined,
            search_term: event.args.query,
            items: undefined,
            content_type: undefined,
            content_id: undefined,
            item_name: undefined,
            nav_from: undefined,
            nav_to: undefined,
            parent_content_type: undefined,
            parent_content_id: undefined,
          });

          const searchResults = event.args.results;
          const thematic =
            this.selectedThematic && this.selectedThematic !== "sdm_default"
              ? this.selectedThematic
              : "";
          if (
            this.selectedThematic &&
            this.selectedThematic !== "" &&
            this.selectedThematic !== "sdm_default"
          ) {
            const pois = this.createPOIListForAnalytics(
              searchResults,
              this.selectedThematic,
            );
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "woo_poi_search_list",
              click_location: undefined,
              content_type: thematic,
              items: pois,
              _clear: true,
              search_term: undefined,
              content_id: undefined,
              item_name: undefined,
              nav_from: undefined,
              nav_to: undefined,
              parent_content_type: undefined,
              parent_content_id: undefined,
            });
          } else {
            /* As far as possible avoid calling /search endpoint */
            if (searchResults && searchResults.length > 0) {
              // const refs: string = searchResults.map((element: SearchResult) => `ref:${element.ref}`).join(',');
              const ids: string = searchResults
                .map((element: SearchResult) => element.id)
                .join(",");
              this.getPOIExtendedData(ids)
                .then((pois) => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: "woo_poi_search_list",
                    click_location: undefined,
                    content_type: thematic,
                    items: pois,
                    _clear: true,
                    search_term: undefined,
                    content_id: undefined,
                    item_name: undefined,
                    nav_from: undefined,
                    nav_to: undefined,
                    parent_content_type: undefined,
                    parent_content_id: undefined,
                  });
                })
                .catch((error) => {
                  console.error("Error while searching POIs:", error);
                });
            }
          }
        }
        if (event.event === WidgetEvents.SEARCH_RESULT_ITEM_SELECTED) {
          const poi: any = event.args;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "woo_poi_search_clic",
            click_location: undefined,
            content_type: poi.properties["thematic"]
              ? poi.properties["thematic"]
              : "",
            content_id: poi.properties["POI_ID"],
            item_name: poi.properties["name"],
            items: undefined,
            search_term: undefined,
            nav_from: undefined,
            nav_to: undefined,
            parent_content_type: undefined,
            parent_content_id: undefined,
          });
        }
        if (event.event === WidgetEvents.FIND_ROUTE) {
          if (this.infoWindow) {
            this.infoWindow.close();
          }
          // TO DO : REVISIT DIRTY FIX FOR ORIGIN GETTING CLEARED IN RENDERER
          this.resetOriginIfNotFound();
          const poi: any = event.args;
          // Comment this if something goes wrong in multi level warning changes !!!
          if (this.currentPage === Page.KIOSK) {
            this.checkForMultilevelDirections();
          }
          this.mapComponent.showRouteForAllLevels();

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "woo_clic_iti",
            click_location: poi.source || "main_map",
            content_type: poi.destination.properties["thematic"]
              ? poi.destination.properties["thematic"]
              : "",
            content_id: poi.destination.properties["POI_ID"],
            item_name: poi.destination.properties["name"],
            items: undefined,
            search_term: undefined,
            nav_from: undefined,
            nav_to: undefined,
            parent_content_type: undefined,
            parent_content_id: undefined,
          });
        }
        if (event.event === WidgetEvents.NAVIGATION_STARTED) {
          const poi: any = event.args;
          let origin = poi.origin.properties["name"];
          if (poi.origin.id === -1) {
            origin =
              this.isKiosk && this.currentKiosk
                ? `Kiosk ${this.currentKiosk.title}`
                : poi.origin.properties["name"];
          }
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "woo_nav",
            click_location: undefined,
            nav_from: origin,
            nav_to: poi.destination.properties["name"],
            items: undefined,
            search_term: undefined,
            item_name: undefined,
            content_type: undefined,
            content_id: undefined,
            parent_content_type: undefined,
            parent_content_id: undefined,
          });
        }
      },
    );
    this.mapComponent.on(
      MapComponentEvents.INDOOR_POI_ON_MAP_CLICK,
      (poi: any) => {
        // TO DO : REVISIT DIRTY FIX FOR ORIGIN GETTING CLEARED IN RENDERER
        this.resetOriginIfNotFound();
        if (poi) {
          if (!this.mapComponent.indoorMap.isItineraryMode()) {
            this.selectedDestination = poi;
          }

          if (isMobileView()) {
            requestAnimationFrame(() => {
              const closePanelBtn = document.getElementsByClassName(
                "btn btn--retract desktop",
              )[0] as HTMLButtonElement;
              const itineraryPanel = document.getElementsByClassName(
                "woosmap-panel__content itinerary",
              )[0] as HTMLDivElement;
              if (closePanelBtn && !itineraryPanel) {
                closePanelBtn.click();
              }
            });
          }

          window.dataLayer.push({
            event: "woo_poi_map_clic",
            click_location: undefined,
            content_type: poi.properties["thematic"]
              ? poi.properties["thematic"]
              : "",
            content_id: poi.properties["POI_ID"],
            item_name: poi.properties["name"],
            items: undefined,
            search_term: undefined,
            nav_from: undefined,
            nav_to: undefined,
          });
          if (this.mapType === "favorites") {
            if (poi && poi.properties) {
              if (
                poi.properties["Exhibitor_Name"] === undefined ||
                poi.properties["Building"] === undefined ||
                poi.properties["Floorplan_Label"] === undefined
              ) {
                return;
              }
            }
            Promise.all([
              this.getUserFavoriteShops(
                this.loggedInUser,
                ExhibitorStatus.TO_VISIT,
              ),
              this.getUserFavoriteShops(
                this.loggedInUser,
                ExhibitorStatus.VISITED,
              ),
            ])
              .then(([tovisit, visited]) => {
                const favorites = [...tovisit.data, ...visited.data];
                if (favorites && favorites.length > 0) {
                  const isPoiFav = favorites.find(
                    (fav) => fav.exhibitor_id === poi.properties.Salesforce_ID,
                  );
                  poi.properties.visit_status = isPoiFav?.status ?? "to_visit";
                  if (isPoiFav) {
                    this.openCustomInfoWindow(poi, true);
                  } else {
                    this.openCustomInfoWindow(poi, false);
                  }
                } else {
                  this.openCustomInfoWindow(poi, false);
                }
              })
              .catch((error) => {
                console.error("Error in fetching favorites", error);
              });
          } else {
            this.openCustomInfoWindow(poi, false);
          }
        }
      },
    );
    this.mapComponent.on(
      MapComponentEvents.INDOOR_WIDGET_ANALYTICS,
      (event) => {
        if (event.event === WidgetEvents.ITINERARY_MODE_EXITED) {
          this.selectedDestination = undefined;
          if (this.currentPage === Page.KIOSK && this.currentKiosk) {
            // Re-set origin on the renderer after panel state settles,
            // so the widget's userLocation is current for next panel open
            setTimeout(() => {
              this.mapComponent.setOrigin(this.currentKiosk!);
            }, 100);
          } else {
            this.selectedOrigin = undefined;
          }
        }
      },
    );
  }

  async fetchKiosksData() {
    try {
      const kioskModule = await import("../data/kiosk");
      const kioskData = kioskModule.default;
      return kioskData.kiosks;
    } catch (error) {
      console.error("Error importing Kiosk JSON file:", error);
    }
  }
  preloadLinks(): void {
    Promise.all([
      // preloadLink({
      //     url: getConfig().urls.mapJS,
      //     params: { key: getConfig().map.woosmapPublicKey, language: getLocaleLang().toLowerCase(), libraries: getConfig().map.libraries },
      //     as: "script",
      // }),
      preloadLink({
        url: "https://api.woosmap.com/maps/tiles.json",
        params: { key: getConfig().map.woosmapPublicKey },
        as: "fetch",
        crossOrigin: "anonymous",
      }),
      preloadLink({
        url: "https://api.woosmap.com/maps/style/streets.json",
        params: {
          language: getLocaleLang().toLowerCase(),
          key: getConfig().map.woosmapPublicKey,
        },
        as: "fetch",
        crossOrigin: "anonymous",
      }),
      preloadLink({
        url: "https://sdk.woosmap.com/map/assets/sprite@2x.png",
        as: "fetch",
        crossOrigin: "anonymous",
      }),
      preloadLink({
        url: "https://sdk.woosmap.com/map/assets/sprite@2x.json",
        as: "fetch",
        crossOrigin: "anonymous",
      }),
    ]);
  }
  createResetMapElement(): void {
    const $mapContainer = document.getElementById("map");
    const $btnToggle = document.getElementById("btnSwitchStandardView");
    if ($mapContainer && !$btnToggle) {
      const toggleStandardView = document.createElement("button");
      toggleStandardView.id = "btnSwitchStandardView";
      toggleStandardView.classList.add("toggle-standard-view");
      toggleStandardView.innerText =
        getLocale().messages.cta["switch_standard_view"];
      toggleStandardView.addEventListener("click", () => {
        // location.reload();
        window.location.href =
          window.location.origin + window.location.pathname;
      });
      $mapContainer.appendChild(toggleStandardView);
    }
  }
  applyTheme(theme: string): void {
    const { config } = this.state;
    if (
      theme === getConfig().map.themes["default"] ||
      theme === getConfig().map.themes["favorites"]
    ) {
      this.setState({ query: "" });
    }
    if (config) {
      const mapConfig = config.map;
      const updatedMapOptions = {
        ...mapConfig,
        indoorRendererOptions: {
          theme: theme,
          ...(theme !== getConfig().map.themes["default"] &&
            theme !== getConfig().map.themes["favorites"] && {
            baseFilter: `thematic:=${theme} OR ${theme}:=true`,
          }),
        },
        indoorWidgetOptions: {
          ...(theme !== getConfig().map.themes["default"] &&
            theme !== getConfig().map.themes["favorites"] && {
            baseFilter: `thematic:=${theme} OR ${theme}:=true`,
          }),
        },
      };
      const updatedConfig = {
        ...config,
        map: updatedMapOptions,
      };
      this.setState({ config: updatedConfig });
    }
    if (
      this.mapComponent &&
      theme !== getConfig().map.themes["default"] &&
      theme !== getConfig().map.themes["favorites"]
    ) {
      this.mapComponent.setFilter(theme);
    }
  }
  getPOIExtendedData(
    ids?: string,
    filter?: string,
  ): Promise<POIForAnalytics[]> {
    return new Promise<POIForAnalytics[]>((resolve, reject) => {
      const indoorService = new woosmap.map.IndoorService();
      const arrPOIs: POIForAnalytics[] = [];
      const callback = (
        featuresCollection: woosmap.map.GeoJSONFeatureCollection,
      ) => {
        if (
          featuresCollection &&
          featuresCollection.features &&
          featuresCollection.features.length > 0
        ) {
          featuresCollection.features.forEach(
            (element: woosmap.map.GeoJSONFeature) => {
              const properties = element.properties as { [key: string]: any };
              const searchedPOI: POIForAnalytics = {
                item_id: properties["ref"],
                item_name: properties["name"],
                item_category: properties["thematic"],
                quantity: 1,
              };
              arrPOIs.push(searchedPOI);
            },
          );
          resolve(arrPOIs);
        } else {
          reject(new Error("No features found."));
        }
      };
      indoorService.search(
        this.venue.venue_id,
        "",
        callback,
        null,
        ids,
        filter,
      );
    });
  }
  createPOIListForAnalytics(
    results: Array<any>,
    thematic: string,
  ): POIForAnalytics[] {
    const arrPOIs: POIForAnalytics[] = [];
    results.forEach((element: SearchResult) => {
      const searchedPOI: POIForAnalytics = {
        item_id: element.ref,
        item_name: element.name,
        item_category: thematic,
        quantity: 1,
      };
      arrPOIs.push(searchedPOI);
    });
    return arrPOIs;
  }
  loadGoogleTagManager() {
    const gaTrackingId = getConfig().map.GATag;
    if (
      !document.querySelector(
        `script[src="https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}"]`,
      )
    ) {
      (function (w: Window, d: Document, s: string, l: string, i: string) {
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
          "gtm.start": new Date().getTime(),
          event: "gtm.js",
        });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s) as HTMLScriptElement;
        const dl = l !== "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode!.insertBefore(j, f);
      })(window, document, "script", "dataLayer", gaTrackingId);
    }
  }
  computeBoundsOfBuildingSelected(buildingList: Building) {
    if (buildingList["pavilion"]) {
      const mergedBounds = new woosmap.map.LatLngBounds();
      buildingList["pavilion"].forEach((item) => {
        if (
          this.venue &&
          this.venue.buildings &&
          this.venue.buildings.length > 0
        ) {
          const filteredBuilding = this.venue.buildings.filter(
            (building: woosmap.map.Building) => building.ref === item,
          )[0];
          if (filteredBuilding && filteredBuilding.levels.length > 0) {
            const bbox = filteredBuilding.levels.filter(
              (level: woosmap.map.Level) => level.level === 0,
            )[0].bbox;
            mergedBounds.extend({ lng: bbox[0], lat: bbox[1] });
            mergedBounds.extend({ lng: bbox[2], lat: bbox[3] });
          }
        }
      });
      if (!mergedBounds.isEmpty()) {
        const isSouth = buildingList["pavilion"].some((p: string) => SOUTH_PAVILION_REFS.has(p));
        const tilt =
          this.currentPage === Page.KIOSK
            ? DefaultMapOptions.kioskTilt
            : DefaultMapOptions.tilt;
        const heading =
          (this.currentPage === Page.KIOSK
            ? DefaultMapOptions.kioskHeading
            : DefaultMapOptions.heading) + (isSouth ? 180 : 0);
        this.mapComponent.map.fitBounds(mergedBounds);
        this.mapComponent.map.setTilt(tilt);
        this.mapComponent.map.setHeading(heading);
        if (this.currentPage === Page.KIOSK && buildingList.kioskZoom) {
          this.mapComponent.map.setZoom(buildingList.kioskZoom);
        }
      }
    }
  }
  setBoundsForSelectedPav(pav: string, kioskPosition: woosmap.map.LatLngLiteral, isSouth: boolean = false, zoom?: number, panOffset?: number, xPanOffset?: number) {
    const mergedBounds = new woosmap.map.LatLngBounds();

    // If pav is defined, try to include building bbox in bounds
    if (pav !== undefined && this.venue?.buildings?.length > 0) {
      const filteredBuilding = this.venue.buildings.filter(
        (building: woosmap.map.Building) => building.ref === pav,
      )[0];
      if (filteredBuilding && filteredBuilding.levels.length > 0) {
        const bbox = filteredBuilding.levels.filter(
          (level: woosmap.map.Level) => level.level === 0,
        )[0].bbox;
        mergedBounds.extend({ lng: bbox[0], lat: bbox[1] });
        mergedBounds.extend({ lng: bbox[2], lat: bbox[3] });
      }
    }

    // Always include kiosk position and apply map view
    mergedBounds.extend(kioskPosition);
    if (!mergedBounds.isEmpty()) {
      const heading = DefaultMapOptions.kioskHeading + (isSouth ? 180 : 0);
      this.mapComponent.map.fitBounds(mergedBounds);
      this.mapComponent.map.setTilt(DefaultMapOptions.kioskTilt);
      this.mapComponent.map.setHeading(heading);
      if (zoom) {
        this.mapComponent.map.setZoom(zoom);
      }
      if (panOffset !== undefined || xPanOffset !== undefined) {
        this.mapComponent.map.panBy(xPanOffset ?? 0, panOffset ?? 0);
      }
    }
  }
  resetMap() {
    const defaultOptions = {
      bearing: DefaultMapOptions.heading,
      animate: true,
      duration: 500,
    };
    let mergedOptions;
    let zoom: number = 17;
    if (this.currentPage === Page.GENERAL) {
      mergedOptions = {
        ...defaultOptions,
        ...{ center: this.thematicCenter, zoom: 17 },
      };
      zoom = 17;
    } else if (this.currentPage === Page.KIOSK) {
      if (this.currentKiosk) {
        mergedOptions = {
          ...defaultOptions,
          ...{
            center: { lat: this.currentKiosk.lat, lng: this.currentKiosk.lng },
            zoom: DefaultMapOptions.kioskZoom,
            pitch: DefaultMapOptions.kioskTilt,
            bearing: DefaultMapOptions.kioskHeading,
          },
        };
        zoom = 17.5;
      }
    }
    if (mergedOptions && zoom) {
      this.mapComponent.map.flyTo(mergedOptions);
      setTimeout(() => {
        this.mapComponent.map.setZoom(zoom);
      }, 550);
    }
  }
  showMultiLevelWarning() {
    const $mapElement = document.querySelector(".mapboxgl-control-container");
    if ($mapElement) {
      const $infoContainer = document.createElement("div");
      $infoContainer.classList.add("mapboxgl-ctrl-top-center", "fade-in-out");
      setTimeout(() => {
        $infoContainer.classList.add("show");
      }, 200);
      const $info = document.createElement("div");
      $info.id = "map-info";
      $info.innerHTML = getLocale().messages.multilevelwarning;
      $infoContainer.appendChild($info);
      $mapElement.appendChild($infoContainer);
      setTimeout(() => {
        $infoContainer.classList.remove("show");
      }, 10000);
    }
  }
  groupInstructionsByLevel(
    steps: Step[],
  ): { level: number | null; steps: Step[] }[] {
    const instructionsByLevel: { level: number | null; steps: Step[] }[] = [];
    let lastStepsByLevel: { level: number | null; steps: Step[] } = {
      level: null,
      steps: [],
    };
    steps.forEach((step) => {
      if (lastStepsByLevel.level !== step.properties.level) {
        lastStepsByLevel = { level: step.properties.level, steps: [] };
        instructionsByLevel.push(lastStepsByLevel);
      }
      lastStepsByLevel.steps.push(step);
    });
    return instructionsByLevel;
  }
  checkForMultilevelDirections() {
    const directionsResult = this.mapComponent.getDirections() as any;
    if (
      directionsResult &&
      directionsResult.routes &&
      directionsResult.routes.length > 0
    ) {
      const route = directionsResult.routes[0];
      const routeSteps = [];
      if (route.legs && route.legs.length > 0) {
        route.legs.forEach((leg: any) => {
          if (leg && leg.steps) {
            leg.steps.forEach((step: any) => {
              const feature = {
                type: "Feature",
                properties: {
                  id: `${route.legs.indexOf(leg)}_${leg.steps.indexOf(step)}`,
                  level: `Level ${step.start_location.level}`,
                  floor: step.start_location.level,
                  instruction: step.instruction
                    ? step.instruction.summary
                    : null,
                  icon:
                    step.instruction && step.instruction.instruction_type
                      ? step.instruction.instruction_type
                      : "caret-down",
                },
                geometry: {
                  type: "LineString",
                  coordinates: step.polyline,
                },
              };
              routeSteps.push(feature);
            });
          }
        });
        if (routeSteps.length > 0) {
          const levelsGroup = this.groupInstructionsByLevel(routeSteps);
          if (levelsGroup.length > 2) {
            this.showMultiLevelWarning();
          }
        }
      }
    }
  }
  resetTheme(theme: string): void {
    if (this.mapComponent) {
      this.mapComponent.setFilter(theme);
    }
  }
  setBoundsOnDirectionsChanged(
    start: woosmap.map.GeoJSONPoint,
    end: woosmap.map.GeoJSONGeometry,
  ) {
    const routeBounds = new woosmap.map.LatLngBounds();
    routeBounds.extend({
      lat: start.coordinates[1],
      lng: start.coordinates[0],
    });
    if (end.type === "Polygon") {
      end.coordinates[0].forEach((coord) =>
        routeBounds.extend({ lat: coord[1], lng: coord[0] }),
      );
    } else if (end.type === "Point") {
      routeBounds.extend({ lat: end.coordinates[1], lng: end.coordinates[0] });
    }
    this.mapComponent.map.fitBounds(
      routeBounds,
      { top: 20, left: 390, right: 70, bottom: 20 },
      true,
    );
  }
  async getUserFavoriteShops(token: string, status: string): Promise<any> {
    try {
      const response = await fetch(
        `${getConfig().urls.lambdaExternal}?token=${token}&status=${status}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (response.status === 400) {
        const errorData = await response.json();
        console.warn("Bad Request: 400", errorData.message);
        return { data: [], unauthorized: true };
      }
      if (response.status === 401) {
        const errorData = await response.json();
        console.warn("Unauthorized: 401", errorData.message);
        return { data: [], unauthorized: true };
      }
      if (!response.ok) {
        const errorData = await response.json();
        showToast(getLocale().messages.api_error, "error");
        throw new Error(`HTTP error: ${response.status}, ${errorData.message}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error occurred:", error);
      return { data: [] };
    }
  }
  getPOIDataForFavorites(
    favorites: string[],
  ): Promise<woosmap.map.GeoJSONFeatureCollection> {
    return new Promise<woosmap.map.GeoJSONFeatureCollection>(
      (resolve, reject) => {
        const indoorService = new woosmap.map.IndoorService();
        if (favorites && favorites.length > 0) {
          const advanced_filter = favorites
            .map((favorite) => `Salesforce_ID:=${favorite}`)
            .join(" OR ");

          const callback = (
            featuresCollection: woosmap.map.GeoJSONFeatureCollection,
          ) => {
            if (
              featuresCollection &&
              featuresCollection.features &&
              featuresCollection.features.length > 0
            ) {
              resolve(featuresCollection);
            } else {
              reject(new Error("No features found."));
            }
          };
          indoorService.search(
            this.venue.venue_id,
            "",
            callback,
            null,
            null,
            advanced_filter,
          );
        }
      },
    );
  }
  showUserFavoritesOnMap(
    favorites: string[],
    shopStatusMapping: Map<string, ExhibitorStatus>,
  ) {
    if (favorites && favorites.length > 0) {
      this.getPOIDataForFavorites(favorites).then((featuresCollection) => {
        const enrichedGeoJson = this.enrichFeaturesWithStatus(
          featuresCollection,
          shopStatusMapping,
        );
        this.mapComponent.indoorMap.setWidgetMapMode(
          "favorites",
          enrichedGeoJson,
        );
        this.mapComponent.plotUserFavoritesOnMap(enrichedGeoJson.features);
      });
    }
  }
  openCustomInfoWindow(feature: any, isFav: boolean = false): void {
    if (feature) {
      if (this.infoWindow) {
        this.infoWindow.close();
      }
      this.infoWindow = new woosmap.map.InfoWindow({
        content: "",
        disableAutoPan: true,
      });
      const logo = feature.properties["woosmap:logo"];
      const logoImageStyle = `background-image: url(${logo})`;
      const poiName = feature.properties["Exhibitor_Name"]
        ? feature.properties["Exhibitor_Name"]
        : feature.properties[`name:${getLocaleLang().toLowerCase()}`]
          ? feature.properties[`name:${getLocaleLang().toLowerCase()}`]
          : feature.properties["name"];
      const poiPav = feature.properties["Building"] ? displayBuilding(feature.properties["Building"]) : undefined;
      const poiFloorLabel = feature.properties["Floorplan_Label"] || "";
      const poiInfo = `${poiPav ? `Pav ${poiPav}` : ""} ${poiFloorLabel} ${getLocale().messages.level} ${feature.properties["level"]}`;
      const status = feature.properties["visit_status"];
      let customHTML = "";
      const favText = isFav
        ? `- ${getLocale().messages.remove_favorites}`
        : `+ ${getLocale().messages.add_favorites}`;
      if (this.loggedInUser && this.mapType === "favorites") {
        customHTML = `<div class="infowindow-wrapper-favs">
                <div class="infowindow-logo" style="${logoImageStyle}"></div>
                <div class="infowindow-content"><span class="title">${poiName}</span>
                <span class="pav-info">${poiInfo}</span>
                <div class="favs-action">
                    <span class="favs-link">${favText}</span>
                    ${isFav
            ? `
                    <label class="checkbox">
                        <input type="checkbox" id="chkMarkVisited" ${status === ExhibitorStatus.VISITED ? "checked" : ""} />
                        <span class="checkmark"></span>
                        ${getLocale().messages.to_visit}
                    </label>`
            : ``
          }
                </div>
                <hr class="infowindow-divider"/>
                <div class="infowindow-action-links">
                <div class="icon-destination">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" data-testid="icon-marker-filled" width="24" height="24" class="icon icon__destination"><path d="M14 2.944a7.785 7.785 0 0 0-7.785 7.785c0 5.594 7.054 8.562 7.054 13.689 0 .419.344.638.731.638s.731-.227.731-.638c0-5.127 7.054-8.095 7.054-13.689A7.785 7.785 0 0 0 14 2.944ZM14 14a3.126 3.126 0 1 1 0-6.252A3.126 3.126 0 0 1 14 14Z" fill="#f50057"></path></svg>
                    <span>${getLocale().messages.destination}</span>
                </div>
                <div class="icon-origin">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" data-testid="icon-position-filled" width="24" height="24" class="icon icon__origin"><path d="M14 6.131a7.869 7.869 0 1 0 0 15.738A7.869 7.869 0 0 0 14 6.13Zm0 10.996a3.126 3.126 0 1 1 0-6.252 3.126 3.126 0 0 1 0 6.252Z" fill="#5586ff"></path></svg>
                    <span>${getLocale().messages.origin}</span>
                </div>
            <div>
                </div></div></div></div>`;
      } else {
        customHTML = `<div class="infowindow-wrapper-gen">
            <div class="infowindow-logo" style="${logoImageStyle}"></div>
            <div class="infowindow-content"><span class="title">${poiName}</span>
            <span class="pav-info">${poiInfo}</span>
            <div class="infowindow-action-links">
                <div class="icon-destination">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" data-testid="icon-marker-filled" width="24" height="24" class="icon icon__destination"><path d="M14 2.944a7.785 7.785 0 0 0-7.785 7.785c0 5.594 7.054 8.562 7.054 13.689 0 .419.344.638.731.638s.731-.227.731-.638c0-5.127 7.054-8.095 7.054-13.689A7.785 7.785 0 0 0 14 2.944ZM14 14a3.126 3.126 0 1 1 0-6.252A3.126 3.126 0 0 1 14 14Z" fill="#f50057"></path></svg>
                    <span>${getLocale().messages.destination}</span>
                </div>
                <div class="icon-origin">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" data-testid="icon-position-filled" width="24" height="24" class="icon icon__origin"><path d="M14 6.131a7.869 7.869 0 1 0 0 15.738A7.869 7.869 0 0 0 14 6.13Zm0 10.996a3.126 3.126 0 1 1 0-6.252 3.126 3.126 0 0 1 0 6.252Z" fill="#5586ff"></path></svg>
                    <span>${getLocale().messages.origin}</span>
                </div>
            <div>
            </div></div>`;
      }
      this.infoWindow.setContent(customHTML);
      if (isFav) {
        this.infoWindow.setOffset(new woosmap.map.Point(0, -30));
      }
      const pos = getFeaturePosition(feature);
      this.infoWindow.open(this.mapComponent.map, pos);
      if (this.mapType === "favorites") {
        document
          .getElementsByClassName("favs-link")[0]
          .addEventListener("click", () => {
            const status = isFav
              ? ExhibitorStatus.NONE
              : ExhibitorStatus.TO_VISIT;
            this.flagUserFavorites(feature, status).then(() => {
              const message = isFav
                ? getLocale().messages.remove_favorites_toast
                : getLocale().messages.add_favorites_toast;
              const toastType = "success";
              this.mapComponent.unselectPOI();
              showToast(message, toastType);
              Promise.all([
                this.getUserFavoriteShops(
                  this.loggedInUser,
                  ExhibitorStatus.TO_VISIT,
                ),
                this.getUserFavoriteShops(
                  this.loggedInUser,
                  ExhibitorStatus.VISITED,
                ),
              ])
                .then(([tovisit, visited]) => {
                  const favorites = [...tovisit.data, ...visited.data];
                  if (favorites && favorites.length > 0) {
                    const shopStatusMapping = this.buildStatusMap(favorites);
                    const favoriteShops = favorites.map(
                      (favorite: any) => favorite.exhibitor_id,
                    );
                    this.showUserFavoritesOnMap(
                      favoriteShops,
                      shopStatusMapping,
                    );
                  } else {
                    this.mapComponent.removeMarkersForFavs();
                    // Sync widget state so the empty-state static toast can render
                    if (this.mapType === "favorites") {
                      this.mapComponent.indoorMap.setWidgetMapMode("favorites", undefined, false);
                    }
                  }
                  if (this.infoWindow) {
                    this.infoWindow.close();
                  }
                })
                .catch((error) => {
                  console.error("Error in fetching favorites", error);
                });
            });
          });
      }
      document
        .getElementsByClassName("icon-destination")[0]
        ?.addEventListener("click", () => {
          if (this.selectedOrigin && this.selectedOrigin.id === feature.id) {
            this.selectedOrigin = undefined;
          }
          this.selectedDestination = feature;
          this.mapComponent.indoorMap.showItinerary(
            this.selectedOrigin,
            feature,
          );
        });

      document
        .getElementsByClassName("icon-origin")[0]
        ?.addEventListener("click", () => {
          if (
            this.selectedDestination &&
            this.selectedDestination.id === feature.id
          ) {
            this.selectedDestination = undefined;
          }
          this.selectedOrigin = feature;
          this.mapComponent.unselectPOI();
          this.mapComponent.indoorMap.showItinerary(
            feature,
            this.selectedDestination,
          );
        });
      const favCheckbox =
        document.querySelector<HTMLInputElement>("#chkMarkVisited");
      if (favCheckbox) {
        favCheckbox.addEventListener("change", (event) => {
          const isChecked = (event.target as HTMLInputElement).checked;

          if (isChecked) {
            this.flagUserFavoritesAsPerStatus(feature, ExhibitorStatus.VISITED);
          } else {
            this.flagUserFavoritesAsPerStatus(
              feature,
              ExhibitorStatus.TO_VISIT,
            );
          }
        });
      }

      let flyToOptions = {
        center: pos,
        padding: computePaddingForPOIPanel(true),
      };
      flyToOptions =
        this.mapComponent.map.getZoom() < 18
          ? { ...flyToOptions, zoom: 18 }
          : flyToOptions;
      this.mapComponent.map.flyTo(flyToOptions);
    }
  }
  async flagUserFavorites(
    feature: GeoJSONFeature,
    status: string,
  ): Promise<any> {
    try {
      const body = {
        token: this.loggedInUser,
        exhibitorIds: [feature.properties["Salesforce_ID"]],
        status,
      };
      const response = await fetch(getConfig().urls.lambdaExternal, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        showToast(getLocale().messages.api_error, "error");
        throw new Error(`Request failed with status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error occurred:", error);
      return Promise.reject(`Fetch failed: ${error}`);
    }
  }
  displayKioskElements() {
    const $mapContainer = document.getElementById("map") as HTMLElement;
    $mapContainer.style.visibility = "visible";
    const $kioskHeader = document.getElementsByClassName("kiosk-header");
    if ($kioskHeader && $kioskHeader[0]) {
      ($kioskHeader[0] as HTMLElement).style.visibility = "visible";
    }
    const $mapSwitcher = document.getElementsByClassName("button-section");
    if ($mapSwitcher && $mapSwitcher[0]) {
      ($mapSwitcher[0] as HTMLElement).style.visibility = "visible";
    }
    this.mapComponent.indoorMap.setWidgetMapMode("kiosk");
    $mapContainer.addEventListener("click", (e: Event) => {
      if ((e.target as Element)?.closest(".mapboxgl-ctrl-compass")) {
        this.kioskComponent?.startSessionCountdown();
      }
    }, true);
  }
  restrictKioskUserInteractions() {
    document.addEventListener("contextmenu", (event: Event) => {
      event.preventDefault();
    });

    document.addEventListener("touchstart", (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    }, { passive: false });
  }
  initThematicComponent() {
    let defaultTheme = this.state.config?.map.indoorRendererOptions.theme || "";
    if (
      defaultTheme &&
      defaultTheme !== "" &&
      defaultTheme !== getConfig().map.themes["default"] &&
      defaultTheme !== getConfig().map.themes["favorites"]
    ) {
      defaultTheme = `thematic:=${defaultTheme.substring(0, defaultTheme.indexOf("_"))}`;
    }
    this.thematicComponent = new ThematicComponent({
      $target: document.querySelector("button-section") as HTMLElement,
      initialState: {
        query: this.state.query?.includes("thematic")
          ? this.state.query
          : defaultTheme,
        showDescription: true,
      },
    });
    this.thematicComponent.on(
      ThematicComponentEvents.THEMATIC_SELECTED,
      (thematic: string) => {
        if (this.currentPage === Page.KIOSK) {
          this.kioskComponent.startSessionCountdown();
        }
        const selectedTheme = thematic.split("=")[1];
        this.selectedThematic = selectedTheme;
        this.itemsForThematicSelected = [];
        this.mapComponent.setFilter(selectedTheme);
        this.mapComponent.setTheme(
          `${selectedTheme}_${getConfig().map.eventYear}`,
        );
        this.mapComponent.unselectPOI();
        this.mapType = "thematics";
        this.selectedDestination = undefined;
        this.selectedOrigin = undefined;
        //TO DO ---> IS THIS MODE NEEDED?
        this.mapComponent.indoorMap.setWidgetMapMode("thematic");
        this.mapComponent.removeMarkersForFavs();
        if (this.infoWindow) {
          this.infoWindow.close();
        }
        const initialCenter = getConfig().map.mapOptions.center;
        if (this.mapComponent.map.getCenter() !== initialCenter) {
          this.mapComponent.map.panTo(this.thematicCenter);
          setTimeout(() => {
            this.mapComponent.map.setZoom(DefaultMapOptions.zoom);
          }, 500);
        }
        if (
          this.selectedThematic &&
          this.selectedThematic !== `sdm_default_${getConfig().map.eventYear}`
        ) {
          this.getPOIExtendedData(undefined, thematic)
            .then((pois) => {
              window.dataLayer = window.dataLayer || [];
              this.itemsForThematicSelected = pois;
              window.dataLayer.push({
                event: "woo_filter",
                click_location: undefined,
                content_type: "thematic",
                content_id: this.selectedThematic,
                items: pois,
                _clear: true,
                search_term: undefined,
                item_name: undefined,
                nav_from: undefined,
                nav_to: undefined,
                parent_content_type: undefined,
                parent_content_id: undefined,
              });
            })
            .catch((error) => {
              console.error("Error while searching POIs:", error);
            });
        }
      },
    );
    this.thematicComponent.on(
      ThematicComponentEvents.BUILDING_SELECTED,
      (buildingList: Building) => {
        if (this.currentPage === Page.KIOSK) {
          this.kioskComponent.startSessionCountdown();
          this.itemsForThematicSelected = [];
        }
        if (buildingList) {
          if (
            buildingList &&
            buildingList.ref &&
            buildingList.ref === "pav2_4xx"
          ) {
            // TO DO: OBSERVE WHY BOUNDS COME WRONG FROM BACKEND. TEMP FIX TO ADJUST IN FRONT END.
            this.mapComponent.map.panTo({
              lat: buildingList.center.lat,
              lng: buildingList.center.lng,
            });
          } else {
            this.computeBoundsOfBuildingSelected(buildingList);
          }
          const thematic =
            this.selectedThematic && this.selectedThematic !== "sdm_default"
              ? this.selectedThematic
              : "";
          window.dataLayer.push({
            event: "woo_filter",
            click_location: undefined,
            content_type: "pav",
            content_id: buildingList.title,
            parent_content_type: "thematic",
            parent_content_id: thematic,
            items:
              this.itemsForThematicSelected &&
                this.itemsForThematicSelected.length > 0
                ? this.itemsForThematicSelected
                : undefined,
            search_term: undefined,
            item_name: undefined,
            nav_from: undefined,
            nav_to: undefined,
          });
        } else {
          if (this.currentPage === Page.KIOSK && this.currentKiosk) {
            const kiosk = this.currentKiosk as any;
            this.setBoundsForSelectedPav(
              kiosk.ref,
              { lat: kiosk.lat, lng: kiosk.lng },
              kiosk.isSouth ?? false,
              kiosk.zoom,
              kiosk.panOffset,
              kiosk.xPanOffset
            );
          } else {
            this.resetMap();
          }
        }
      },
    );
    this.thematicComponent.on(
      ThematicComponentEvents.THEMATIC_UNSELECTED,
      (theme: string) => {
        if (this.currentPage === Page.KIOSK) {
          this.kioskComponent.startSessionCountdown();
        }
        this.resetTheme(theme);
        this.mapComponent.setTheme(theme);
        this.mapComponent.unselectPOI();
        this.mapComponent.removeMarkersForFavs();
        this.mapType = "general";
        if (this.infoWindow) {
          this.infoWindow.close();
        }
        const initialCenter = getConfig().map.mapOptions.center;
        if (this.mapComponent.map.getCenter() !== initialCenter) {
          this.mapComponent.map.panTo(this.thematicCenter);
          setTimeout(() => {
            this.mapComponent.map.setZoom(DefaultMapOptions.zoom);
          }, 500);
        }
        if (theme === getConfig().map.themes.favorites) {
          this.mapType = "favorites";
          const query = this.state.query;
          if (query && query.includes("token")) {
            const token = query.split(":=")[1];
            this.loggedInUser = token;
            const fetchFavorites = (token: string) => {
              Promise.all([
                this.getUserFavoriteShops(
                  this.loggedInUser,
                  ExhibitorStatus.TO_VISIT,
                ),
                this.getUserFavoriteShops(
                  this.loggedInUser,
                  ExhibitorStatus.VISITED,
                ),
              ])
                .then(([tovisit, visited]) => {
                  const isUnauthorized = tovisit.unauthorized || visited.unauthorized;
                  const favorites = [...tovisit.data, ...visited.data];
                  if (favorites && favorites.length > 0) {
                    const shopStatusMapping = this.buildStatusMap(favorites);
                    const favoriteShops = favorites.map(
                      (favorite: any) => favorite.exhibitor_id,
                    );
                    this.showUserFavoritesOnMap(
                      favoriteShops,
                      shopStatusMapping,
                    );
                  }
                  else if (isUnauthorized) {
                    this.mapComponent.indoorMap.setWidgetMapMode("favorites", undefined, true);
                  }
                  else {
                    // Logged in but empty favorites list — set mode so the empty-state static toast can render
                    this.mapComponent.indoorMap.setWidgetMapMode("favorites", undefined, false);
                  }
                })
                .catch((error) => {
                  console.error("Error in fetching favorites", error);
                });
            };

            if (token) {
              fetchFavorites(token);
            }
          }
        } else {
          this.mapComponent.indoorMap.setWidgetMapMode("general");
        }
      },
    );
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("token") && urlParams.has("path")) {
      const path = urlParams.get("path");
      if (path === "user_exhibitors") {
        this.thematicComponent.switchMap("favorites");
      }
    }
  }
  manageNativeKeyboardIssues(): void {
    const ulElement = document.getElementById("autoComplete_list_1");
    const searchInput = document.querySelector(".autoComplete_wrapper > input");

    const handleFocusIn = () => {
      if (ulElement && searchInput && window.visualViewport) {
        disableBodyScroll(ulElement); // Prevent background scrolling

        const viewportHeight = window.visualViewport.height;
        const inputRect = searchInput.getBoundingClientRect();
        const availableHeight = viewportHeight - inputRect.bottom;
        ulElement.style.maxHeight = `${availableHeight}px !important`;
      }
    };

    const handleFocusOut = () => {
      if (ulElement) {
        enableBodyScroll(ulElement); // Restore background scrolling
        ulElement.style.maxHeight = "233px"; // Reset height
      }
    };

    if (searchInput) {
      searchInput.addEventListener("focus", handleFocusIn);
      searchInput.addEventListener("blur", handleFocusOut);
    }
  }
  resetOriginIfNotFound() {
    const panels = document.getElementsByClassName("woosmap-indoor-panel");
    const search =
      panels.length > 0 ? document.getElementsByTagName("input")[0] : null;
    if (search && search.value === "") {
      if (this.selectedOrigin) {
        const origin = `${(this.selectedOrigin.properties as any)["name"]} ${getLocale().indoor["at Level"]} (${(this.selectedOrigin.properties as any)["level"]})`;
        search.value = origin;
      }
    }
  }
  flagUserFavoritesAsPerStatus(
    feature: woosmap.map.GeoJSONFeature,
    status: string,
  ) {
    this.flagUserFavorites(feature, status).then(() => {
      const message =
        status === ExhibitorStatus.VISITED
          ? getLocale().messages.visited_toast
          : getLocale().messages.unvisited_toast;
      const toastType = "success";
      this.mapComponent.unselectPOI();
      showToast(message, toastType);
      Promise.all([
        this.getUserFavoriteShops(this.loggedInUser, ExhibitorStatus.TO_VISIT),
        this.getUserFavoriteShops(this.loggedInUser, ExhibitorStatus.VISITED),
      ])
        .then(([tovisit, visited]) => {
          const favorites = [...tovisit.data, ...visited.data];
          if (favorites && favorites.length > 0) {
            const shopStatusMapping = this.buildStatusMap(favorites);
            const favoriteShops = favorites.map(
              (favorite: any) => favorite.exhibitor_id,
            );
            this.showUserFavoritesOnMap(favoriteShops, shopStatusMapping);
          } else {
            this.mapComponent.removeMarkersForFavs();
          }
          if (this.infoWindow) {
            this.infoWindow.close();
          }
        })
        .catch((error) => {
          console.error("Error in fetching favorites", error);
        });
    });
  }
  buildStatusMap(data: ExhibitorStatusMapping[]): Map<string, ExhibitorStatus> {
    return new Map(data.map((item) => [item.exhibitor_id, item.status]));
  }
  enrichFeaturesWithStatus(
    featureCollection: woosmap.map.GeoJSONFeatureCollection,
    statusMap: Map<string, ExhibitorStatus>,
  ): woosmap.map.GeoJSONFeatureCollection {
    return {
      ...featureCollection,
      features: featureCollection.features.map(
        (feature: woosmap.map.GeoJSONFeature) => {
          const shopId = (feature.properties as any).Salesforce_ID;
          const status = shopId ? statusMap.get(shopId) : undefined;

          return {
            ...feature,
            properties: {
              ...feature.properties,
              visit_status: status ?? ExhibitorStatus.TO_VISIT, // safe default
            },
          };
        },
      ),
    };
  }
}
